
class TextSplitter {
  static #span(className, ...content) {
    const span = document.createElement('span');
    span.className = className;
    span.append(...content);
    return span;
  }

  static #eachWord(el, wrap) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    for (const text of texts) {
      const parts = text.data.split(/(\s+)/).filter(Boolean);
      text.replaceWith(...parts.map((part) => (/^\s/.test(part) ? part : wrap(part))));
    }
  }

  static splitWords(el) {
    const text = el.textContent.trim();
    if (!text) return;

    el.setAttribute('aria-label', text);
    let index = 0;
    this.#eachWord(el, (word) => {
      const inner = this.#span('word-inner', word);
      inner.style.setProperty('--word-index', index++);
      return this.#span('word', inner);
    });
    el.style.setProperty('--word-step', `${Math.min(60, 600 / (index - 1))}ms`);
    el.style.setProperty('--word-count', index);
    el.dataset.textSplit = 'words';
  }

  static processAll() {
    document.querySelectorAll('[data-animate="text-words"]:not([data-text-split]):not(.animated), [data-animate="text-lit"]:not([data-text-split])').forEach(el => this.splitWords(el));
  }
}

function onScreen(el) {
  if (el.checkVisibility?.({ contentVisibilityAuto: true }) === false) return false;
  const box = el.getBoundingClientRect();
  return box.bottom > 0 && box.right > 0 && box.top < innerHeight && box.left < innerWidth;
}

const WAITING = '[data-animate]:not(.animated):not([data-animate="none"])';

class AnimationController {
  #controller = new AbortController();
  #sections;
  #entrances;
  #sectionOf = new Map();
  #waiting = new Map();
  #near = new Set();
  #mutation;
  #animated = new Set();
  #pendingDelays = new Map();
  #staggered = new WeakSet();

  constructor() {
    if (this.#prefersReduced()) {
      this.#disable();
      this.#watchReducedMotion();
      return;
    }

    this.#watchReducedMotion();
    this.#init();
  }

  get signal() {
    return this.#controller.signal;
  }

  #init() {
    const setup = () => {
      this.#revealOnScreen();
      document.body.classList.add('animations-ready');
      TextSplitter.processAll();
      this.#observeAll();
      this.#watchMutations();
      this.#watchDesignMode();
      this.#watchResize();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(setup, { timeout: 150 });
    } else {
      setTimeout(setup, 0);
    }
  }

  #revealOnScreen() {
    const seen = [...document.querySelectorAll('[data-animate]:not([data-animate="shine"])')].filter(onScreen);
    for (const el of seen) el.classList.add('animated');
  }

  #observeAll() {
    for (const [el, section] of this.#sectionOf) {
      if (!el.isConnected || !section.contains(el) || el.classList.contains('animated')) this.#release(el);
    }
    for (const el of document.querySelectorAll(WAITING)) {
      if (!this.#sectionOf.has(el)) this.#wait(el);
    }
  }

  #wait(el) {
    const section = el.closest('dialog, .shopify-section') ?? el;
    this.#sectionOf.set(el, section);
    if (!this.#waiting.has(section)) {
      this.#waiting.set(section, new Set());
      this.#createObservers();
      this.#sections.observe(section);
    }
    this.#waiting.get(section).add(el);
    if (this.#near.has(section)) this.#entrances.observe(el);
  }

  #release(el) {
    const section = this.#sectionOf.get(el);
    if (!section) return;
    this.#sectionOf.delete(el);
    this.#entrances.unobserve(el);
    const waiting = this.#waiting.get(section);
    waiting.delete(el);
    if (waiting.size) return;
    this.#waiting.delete(section);
    this.#near.delete(section);
    this.#sections.unobserve(section);
    if (!this.#waiting.size) this.#letGo();
  }

  #createObservers() {
    if (this.#sections) return;
    this.#entrances = new IntersectionObserver((entries) => {
      const arriving = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
      for (const el of arriving) this.#stagger(el.closest('[data-stagger-children]'));
      for (const el of arriving) this.#animate(el);
    });
    this.#sections = new IntersectionObserver((entries) => {
      for (const { target, isIntersecting } of entries) {
        const waiting = this.#waiting.get(target);
        if (!waiting) continue;
        if (isIntersecting) this.#near.add(target);
        else this.#near.delete(target);
        for (const el of waiting) {
          if (isIntersecting) this.#entrances.observe(el);
          else this.#entrances.unobserve(el);
        }
      }
    }, { rootMargin: '50% 0px' });
  }

  #letGo() {
    this.#sections?.disconnect();
    this.#entrances?.disconnect();
    this.#sections = null;
    this.#entrances = null;
    this.#sectionOf.clear();
    this.#waiting.clear();
    this.#near.clear();
  }

  #animate(el) {
    this.#release(el);
    if (this.#animated.has(el) || el.classList.contains('animated')) return;
    this.#animated.add(el);

    let type = el.getAttribute('data-animate');

    if (type === 'inherit') {
      type = this.#resolveInherit(el);
      if (type && type !== 'none') {
        el.setAttribute('data-animate', type);
      } else {
        for (const figure of el.querySelectorAll('[data-count-up]')) this.#countUp(figure);
        el.classList.add('animated');
        return;
      }
    }

    const delay = parseInt(el.style.getPropertyValue('--animate-delay'), 10) || 0;

    if (delay > 0) {
      const timer = setTimeout(() => {
        this.#pendingDelays.delete(el);
        this.#trigger(el);
      }, delay);
      this.#pendingDelays.set(el, timer);
    } else {
      this.#trigger(el);
    }
  }

  #resolveInherit(el) {
    const container = el.closest('[data-container-animate]');
    if (container) {
      const containerType = container.dataset.containerAnimate;
      if (containerType && containerType !== 'none') return containerType;
      if (containerType === 'none') return 'none';
    }
    return window.Scale.settings.animations_preset;
  }

  #trigger(el) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (el.classList.contains('animated')) return;
        for (const figure of el.querySelectorAll('[data-count-up]')) this.#countUp(figure);
        el.classList.add('animated', 'animating');
        requestAnimationFrame(() => setTimeout(() => this.#settle(el)));
      });
    });
  }

  #settle(el) {
    const entrance = el.getAnimations({ subtree: true })
      .filter((animation) => animation.timeline === document.timeline && animation.effect?.getComputedTiming().endTime !== Infinity);
    Promise.allSettled(entrance.map((animation) => animation.finished))
      .then(() => el.classList.remove('animating'));
  }

  #countUp(el) {
    const text = el.textContent;
    const value = parseFloat(text);
    const decimals = text.split('.')[1]?.length ?? 0;
    const duration = parseFloat(getComputedStyle(el).getPropertyValue('--duration-slow')) * 2;
    const start = performance.now();
    el.style.minInlineSize = `${el.getBoundingClientRect().width}px`;
    const step = (now) => {
      const progress = Math.min(Math.max((now - start) / duration, 0), 1);
      el.textContent = progress < 1 ? (value * (1 - (1 - progress) ** 3)).toFixed(decimals) : text;
      if (progress < 1) requestAnimationFrame(step);
    };
    step(start);
  }

  #cancelPending(el) {
    if (this.#pendingDelays.has(el)) {
      clearTimeout(this.#pendingDelays.get(el));
      this.#pendingDelays.delete(el);
    }
  }

  #stagger(container) {
    if (!container || this.#staggered.has(container)) return;
    this.#staggered.add(container);

    const ROW_TOLERANCE = 8;
    const step = parseInt(container.dataset.staggerChildren, 10) || 100;
    const children = container.querySelectorAll(':scope > * [data-animate]:not(.animated), :scope > [data-animate]:not(.animated)');

    if (children.length < 2) return;

    const sorted = [...children]
      .map(el => ({ el, top: el.getBoundingClientRect().top, left: el.getBoundingClientRect().left }))
      .sort((a, b) => (a.top - b.top) || (a.left - b.left));

    let rowIndex = -1;
    let lastTop = -Infinity;

    for (const { el, top } of sorted) {
      if (top - lastTop > ROW_TOLERANCE) {
        rowIndex = 0;
        lastTop = top;
      } else {
        rowIndex++;
      }
      el.style.setProperty('--animate-delay', `${rowIndex * step}ms`);
    }
  }

  #watchMutations() {
    let pending = false;

    this.#mutation = new MutationObserver((mutations) => {
      if (pending) return;

      let hasNew = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.hasAttribute?.('data-animate') || node.querySelector?.('[data-animate]'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (!hasNew) return;

      pending = true;
      requestAnimationFrame(() => {
        TextSplitter.processAll();
        requestAnimationFrame(() => {
          pending = false;
          this.#staggered = new WeakSet();
          this.#observeAll();
        });
      });
    });

    this.#mutation.observe(document.body, { childList: true, subtree: true });
  }

  #watchDesignMode() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        TextSplitter.processAll();
        this.#staggered = new WeakSet();
        this.#observeAll();
      }, 100);
    }, { signal: this.signal });

    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target;
      if (!section) return;
      section.querySelectorAll('[data-animate]').forEach(el => {
        this.#cancelPending(el);
        this.#release(el);
        this.#animated.delete(el);
      });
    }, { signal: this.signal });
  }

  #watchResize() {
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.#staggered = new WeakSet();
      }, 250);
    }, { signal: this.signal, passive: true });
  }

  #prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #watchReducedMotion() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', (e) => {
      if (e.matches) {
        this.#disable();
        this.#destroy();
      } else {
        this.#enable();
        this.#init();
      }
    }, { signal: this.signal });
  }

  #disable() {
    document.documentElement.classList.add('animations-disabled');
    document.body.classList.remove('animations-ready');
  }

  #enable() {
    document.documentElement.classList.remove('animations-disabled');
  }

  #destroy() {
    this.#letGo();
    this.#mutation?.disconnect();
    this.#mutation = null;
    this.#pendingDelays.forEach(t => clearTimeout(t));
    this.#pendingDelays.clear();
    this.#animated.clear();
    document.body.classList.remove('animations-ready');
  }
}

new AnimationController();
